/**
 * Mobile App Initialization Hook
 * Centralizes all native feature initialization
 */

import { useEffect, useState } from 'react';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';
import { isNativePlatform, isIOS } from '../utils/capacitor';
import { initializePushNotifications } from '../services/pushNotifications';
import { initializeNetworkMonitoring } from '../services/networkStatus';

export const useMobileAppInit = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isNative, setIsNative] = useState(false);

  useEffect(() => {
    const initMobileApp = async () => {
      const native = isNativePlatform();
      setIsNative(native);

      if (!native) {
        setIsInitialized(true);
        return;
      }

      try {
        console.log('Initializing mobile app...');

        // 1. Configure Status Bar
        await configureStatusBar();

        // 2. Configure Keyboard
        await configureKeyboard();

        // 3. Initialize network monitoring
        await initializeNetworkMonitoring();

        // 4. Initialize push notifications
        await initializePushNotifications();

        // 5. Setup app state listeners
        setupAppStateListeners();

        // 6. Hide splash screen after initialization
        await SplashScreen.hide();

        setIsInitialized(true);
        console.log('Mobile app initialized successfully');
      } catch (error) {
        console.error('Error initializing mobile app:', error);
        setIsInitialized(true); // Initialize anyway
      }
    };

    initMobileApp();
  }, []);

  return { isInitialized, isNative };
};

/**
 * Configure status bar appearance
 */
const configureStatusBar = async () => {
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#0F172A' });
    
    if (isIOS()) {
      await StatusBar.setOverlaysWebView({ overlay: false });
    }
  } catch (error) {
    console.error('Error configuring status bar:', error);
  }
};

/**
 * Configure keyboard behavior
 */
const configureKeyboard = async () => {
  try {
    // Setup keyboard listeners
    Keyboard.addListener('keyboardWillShow', (info) => {
      console.log('Keyboard will show with height:', info.keyboardHeight);
    });

    Keyboard.addListener('keyboardDidShow', (info) => {
      console.log('Keyboard shown');
    });

    Keyboard.addListener('keyboardWillHide', () => {
      console.log('Keyboard will hide');
    });

    Keyboard.addListener('keyboardDidHide', () => {
      console.log('Keyboard hidden');
    });
  } catch (error) {
    console.error('Error configuring keyboard:', error);
  }
};

/**
 * Setup app state listeners (background, foreground, etc.)
 */
const setupAppStateListeners = () => {
  try {
    // App state change listener
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('App state changed. Is active:', isActive);
      
      if (isActive) {
        // App came to foreground - sync data if needed
        window.dispatchEvent(new CustomEvent('app:foreground'));
      } else {
        // App went to background - save state
        window.dispatchEvent(new CustomEvent('app:background'));
      }
    });

    // Back button listener (Android)
    App.addListener('backButton', ({ canGoBack }) => {
      if (!canGoBack) {
        // App.exitApp(); // Uncomment to allow exit on back button
      } else {
        window.history.back();
      }
    });

    // URL open listener (deep links)
    App.addListener('appUrlOpen', (event) => {
      console.log('App opened via URL:', event.url);
      
      // Handle deep link
      const slug = event.url.split('.app').pop();
      if (slug) {
        window.location.href = slug;
      }
    });
  } catch (error) {
    console.error('Error setting up app state listeners:', error);
  }
};
